import { useEffect, useRef, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Chip,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Drawer,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import MenuIcon from "@mui/icons-material/Menu";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useLocation } from "react-router-dom";
import DocVideoPlayer from "./components/DocVideoPlayer";
import DocStepCard from "./components/DocStepCard";
import DocTOC from "./components/DocTOC";
import { useScrollSpy } from "./hooks/useScrollSpy";
import { deviceRegistrationGuide } from "./data/deviceRegistrationSteps";
import { usePanelTokens } from "../../../utils/panelTokens";

const GUIDES_MAP = {
  "device-registration": deviceRegistrationGuide,
};

const DocViewer = ({ docId, onBack }) => {
  const theme = useTheme();
  const location = useLocation();
  const T = usePanelTokens();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const videoRef = useRef(null);
  const [tocOpen, setTocOpen] = useState(false); 

  const guide = GUIDES_MAP[docId];
  const stepIds = useMemo(
    () => (guide ? guide.steps.map((s) => s.id) : []),
    [guide]
  );
  const activeStepId = useScrollSpy(stepIds);

  useEffect(() => {
    if (!guide || !location.hash) return;

    const targetId = decodeURIComponent(location.hash.slice(1));
    const timer = window.setTimeout(() => {
      document.getElementById(targetId)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);

    return () => window.clearTimeout(timer);
  }, [guide, location.hash]);

  const handleSeekVideo = (seconds, stepTitle) => {
    const videoEl = document.getElementById("doc-video-section");
    if (videoEl) videoEl.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => {
      if (videoRef.current) videoRef.current.seekTo(seconds, stepTitle);
    }, 350);
  };

  if (!guide) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">Guide not found: {docId}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: T.ground,
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.25,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexShrink: 0,
          borderBottom: `1px solid ${T.border}`,
          backgroundColor: T.ground,
        }}
      >
        <Tooltip title="Back to guides">
          <IconButton
            size="small"
            onClick={onBack}
            sx={{
              border: `1px solid ${T.border}`,
              borderRadius: 1.5,
              p: 0.75,
              color: T.textMuted,
              "&:hover": { backgroundColor: T.hover, color: T.text },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>

        <Breadcrumbs sx={{ fontSize: 13, flex: 1, minWidth: 0 }}>
          <Link
            component="button"
            underline="hover"
            onClick={onBack}
            sx={{
              fontSize: 13,
              color: T.accent,
              cursor: "pointer",
              "&:hover": { color: T.accentHover },
            }}
          >
            Guides
          </Link>
          <Typography
            sx={{
              fontSize: 13,
              color: T.text,
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {guide.title}
          </Typography>
        </Breadcrumbs>

        {isMobile && (
          <Tooltip title="Table of contents">
            <IconButton
              size="small"
              onClick={() => setTocOpen(true)}
              sx={{
                border: `1px solid ${T.border}`,
                borderRadius: 1.5,
                p: 0.75,
                color: T.textMuted,
              }}
            >
              <MenuIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {!isMobile && (
          <Box
            sx={{
              width: 280,
              flexShrink: 0,
              p: 2,
              overflowY: "auto",
              borderRight: `1px solid ${T.border}`,
              backgroundColor: T.ground,
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-thumb": {
                background: T.scrollThumb,
                borderRadius: 2,
              },
            }}
          >
            <DocTOC
              steps={guide.steps}
              activeId={activeStepId}
              onVideoClick={() => handleSeekVideo(0, "")}
            />
          </Box>
        )}

        <Drawer
          anchor="left"
          open={tocOpen}
          onClose={() => setTocOpen(false)}
          PaperProps={{
            sx: {
              width: 280,
              p: 2,
              backgroundColor: T.ground,
              borderRight: `1px solid ${T.border}`,
            },
          }}
        >
          <DocTOC
            steps={guide.steps}
            activeId={activeStepId}
            onVideoClick={() => {
              handleSeekVideo(0, "");
              setTocOpen(false);
            }}
            onClose={() => setTocOpen(false)}
          />
        </Drawer>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: { xs: 2, md: 4, lg: 5 },
            py: 3,
            backgroundColor: T.ground,
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: T.scrollThumb,
              borderRadius: 3,
            },
          }}
        >
          <Box sx={{ maxWidth: 780, mx: "auto" }}>

            <Box
              sx={{
                mb: 4,
                p: 3,
                borderRadius: 2.5,
                border: `1px solid ${T.border}`,
                backgroundColor: T.surface,
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 20, md: 24 },
                  mb: 1,
                  color: T.text,
                  letterSpacing: "-0.025em",
                  lineHeight: 1.3,
                }}
              >
                {guide.title}
              </Typography>

              <Typography
                sx={{
                  fontSize: 14,
                  color: T.textMuted,
                  mb: 2.5,
                  lineHeight: 1.7,
                }}
              >
                {guide.description}
              </Typography>

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip
                  icon={<FormatListNumberedIcon sx={{ fontSize: "13px !important", color: `${T.accent} !important` }} />}
                  label={`${guide.steps.length} steps`}
                  size="small"
                  sx={{
                    fontSize: 12,
                    height: 26,
                    backgroundColor: "rgba(58,142,255,0.1)",
                    color: T.accent,
                    border: "1px solid rgba(58,142,255,0.2)",
                    fontWeight: 500,
                  }}
                />
                <Chip
                  icon={<AccessTimeIcon sx={{ fontSize: "13px !important", color: `${T.textMuted} !important` }} />}
                  label={`${Math.floor(guide.videoDuration / 60)}m ${guide.videoDuration % 60}s`}
                  size="small"
                  sx={{
                    fontSize: 12,
                    height: 26,
                    backgroundColor: T.hover,
                    color: T.textMuted,
                    fontWeight: 500,
                  }}
                />
                <Chip
                  icon={<CalendarTodayIcon sx={{ fontSize: "11px !important", color: `${T.textMuted} !important` }} />}
                  label={`Updated ${guide.lastUpdated}`}
                  size="small"
                  sx={{
                    fontSize: 12,
                    height: 26,
                    backgroundColor: T.hover,
                    color: T.textMuted,
                    fontWeight: 500,
                  }}
                />
              </Box>
            </Box>

            <Box id="doc-video-section" sx={{ mb: 4, scrollMarginTop: 20 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: T.textFaint,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                  }}
                >
                  Overview Video
                </Typography>
                <Box sx={{ flex: 1, height: "1px", backgroundColor: T.border }} />
              </Box>

              <DocVideoPlayer
                ref={videoRef}
                videoUrl={guide.videoUrl}
                title={guide.title}
              />
            </Box>

            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: T.textFaint,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                  }}
                >
                  Step-by-step guide
                </Typography>
                <Box sx={{ flex: 1, height: "1px", backgroundColor: T.border }} />
                <Typography
                  sx={{
                    fontSize: 11,
                    color: T.textFaint,
                    fontWeight: 500,
                  }}
                >
                  {guide.steps.length} steps
                </Typography>
              </Box>

              {guide.steps.map((step) => (
                <DocStepCard
                  key={step.id}
                  step={step}
                  isActive={activeStepId === step.id}
                  onSeekVideo={handleSeekVideo}
                />
              ))}
            </Box>

            <Box
              sx={{
                mt: 2,
                mb: 5,
                p: 3,
                borderRadius: 2.5,
                border: "1px solid rgba(34,197,94,0.25)",
                backgroundColor: "rgba(34,197,94,0.06)",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: "rgba(34,197,94,0.12)",
                  border: "1.5px solid rgba(34,197,94,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <CheckCircleOutlineIcon sx={{ color: T.success, fontSize: 22 }} />
              </Box>
              <Box>
                <Typography
                  sx={{ fontSize: 14, fontWeight: 600, color: T.success, mb: 0.25 }}
                >
                  Guide complete
                </Typography>
                <Typography sx={{ fontSize: 13, color: T.textMuted }}>
                  Your device is now registered and ready to use in the Vecros platform.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DocViewer;
